import { useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Building, Plus, Trash2, Loader2, Download } from "lucide-react";
import { useLocation } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { LocalStorageManager } from "@/lib/storage";
import type { Project } from "@shared/schema";
import { ImportProjectButton } from "./import-project-button";
import { QuoteVersionsBadge } from "./quote-versions-badge";
import { downloadProjectExport } from "@/lib/project-import-export";

export function ProjectList() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null);
  const [exportingId, setExportingId] = useState<number | null>(null);

  const handleExport = async (project: Project) => {
    setExportingId(project.id);
    try {
      await downloadProjectExport(project.id, project.name);
      toast({ title: "Export erstellt", description: `„${project.name}" wurde heruntergeladen.` });
    } catch (err) {
      toast({
        title: "Export fehlgeschlagen",
        description: (err as Error).message || "Unbekannter Fehler",
        variant: "destructive",
      });
    } finally {
      setExportingId(null);
    }
  };

  const { data: allProjects, isLoading } = useQuery<Project[]>({
    queryKey: ["/api/projects"],
  });

  // Abgeschlossene Angebote leben auf einer eigenen Seite (`/completed`) und
  // sollen daher nicht mehr in der Liste der „aktuellen" Projekte auftauchen.
  const projects = useMemo(
    () => (allProjects ?? []).filter((p) => p.status !== "completed"),
    [allProjects],
  );

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest("DELETE", `/api/projects/${id}`),
    onSuccess: (_data, id) => {
      LocalStorageManager.deleteProject(String(id));
      queryClient.invalidateQueries({ queryKey: ["/api/projects"] });
      queryClient.invalidateQueries({ queryKey: ["/api/projects/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/projects/search"] });
      toast({
        title: "Projekt gelöscht",
        description: "Das Projekt wurde erfolgreich entfernt.",
      });
      setDeleteTarget(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Fehler beim Löschen",
        description: error.message || "Das Projekt konnte nicht gelöscht werden.",
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-bauwerk-teal">Aktuelle Projekte</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex justify-between items-center p-4 border rounded-lg">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-3 w-32" />
                  <Skeleton className="h-3 w-24" />
                </div>
                <Skeleton className="h-6 w-20" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!projects || projects.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-bauwerk-teal">Aktuelle Projekte</CardTitle>
          <p className="text-gray-600">Ihre letzten Erfassungen und deren Status</p>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-400 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building className="w-8 h-8 text-white" />
            </div>
            <p className="text-gray-600 mb-4">Noch keine Projekte vorhanden</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                onClick={() => navigate("/project/create")}
                className="bg-bauwerk-orange hover:bg-bauwerk-light-orange"
              >
                <Plus className="w-4 h-4 mr-2" />
                Erstes Projekt erstellen
              </Button>
              <ImportProjectButton />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-bauwerk-mint text-bauwerk-teal";
      case "completed":
        return "bg-green-100 text-green-800";
      case "draft":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "active":
        return "In Bearbeitung";
      case "completed":
        return "Abgeschlossen";
      case "draft":
        return "Entwurf";
      default:
        return status;
    }
  };

  const isDeletingTarget = (id: number) =>
    deleteMutation.isPending && deleteTarget?.id === id;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <CardTitle className="text-bauwerk-teal">Aktuelle Projekte</CardTitle>
              <p className="text-gray-600">Ihre letzten Erfassungen und deren Status</p>
            </div>
            <ImportProjectButton />
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {projects.map((project) => (
              <div
                key={project.id}
                className="flex justify-between items-center p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/project/edit/${project.id}`)}
                data-testid={`project-row-${project.id}`}
              >
                <div className="flex-1">
                  <h4 className="font-semibold text-bauwerk-teal mb-1">
                    {project.name}
                  </h4>
                  <p className="text-sm text-gray-600">{project.address}</p>
                  <p className="text-xs text-gray-500">
                    {project.createdAt ? new Date(project.createdAt).toLocaleDateString('de-DE') : ''}
                  </p>
                  <p className="text-xs text-blue-600 mt-1 font-medium">
                    → Klicken zum Bearbeiten (Schritt {project.currentStep} von 4)
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <QuoteVersionsBadge project={project} />
                  <Badge className={getStatusColor(project.status)}>
                    {getStatusText(project.status)}
                  </Badge>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-gray-500 hover:text-bauwerk-teal hover:bg-bauwerk-mint/40"
                    aria-label={`Projekt ${project.name} exportieren`}
                    disabled={exportingId === project.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleExport(project);
                    }}
                    data-testid={`button-export-project-${project.id}`}
                  >
                    {exportingId === project.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-gray-500 hover:text-red-600 hover:bg-red-50"
                    aria-label={`Projekt ${project.name} löschen`}
                    disabled={isDeletingTarget(project.id)}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(project);
                    }}
                    data-testid={`button-delete-project-${project.id}`}
                  >
                    {isDeletingTarget(project.id) ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open && !deleteMutation.isPending) {
            setDeleteTarget(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Projekt löschen</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie das Projekt{" "}
              <span className="font-semibold">{deleteTarget?.name}</span> wirklich
              unwiderruflich löschen? Alle zugehörigen Fotos werden mit entfernt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (deleteTarget) {
                  deleteMutation.mutate(deleteTarget.id);
                }
              }}
              disabled={deleteMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
              data-testid="button-confirm-delete-project"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Lösche...
                </>
              ) : (
                "Löschen"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
